<template>
  <div
    v-viewport-width="resizeFlickingCamera"
    class="wrapper"
  >
    <Flicking
      ref="flicking"
      :options="combinedOptions"
    >
      <slot />
    </Flicking>
    <span
      v-if="arrows"
      :class="[
        'flicking-arrow-prev flicking-button is-outside is-circle',
        prevClass,
        pageCount === 1 && 'flicking-arrow-is-disabled'
      ]"
    />
    <span
      v-if="arrows"
      :class="[
        'flicking-arrow-next flicking-button is-outside is-circle',
        nextClass,
        pageCount === 1 && 'flicking-arrow-is-disabled'
      ]"
    />
    <div v-if="pagination" :class="['flicking-pagination', paginationClass]" />
  </div>
</template>

<script>
import { Flicking } from '@egjs/vue-flicking';
import { Arrow, AutoPlay, Pagination } from '@egjs/flicking-plugins';

export default {
  components: {
    Flicking,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    arrows: {
      type: Boolean,
      default: true,
    },
    autoplay: {
      type: Number,
      default: 0,
    },
    pagination: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pageCount: 1,
      prevClass: `flicking-arrow-prev-${this.id}`,
      nextClass: `flicking-arrow-next-${this.id}`,
      paginationClass: `flicking-pagination-${this.id}`,
    };
  },
  computed: {
    combinedOptions() {
      return {
        hideBeforeInit: true,
        align: 'prev',
        circular: true,
        noPanelStyleOverride: true,
        ...this.options,
      };
    },
  },
  mounted() {
    const { flicking } = this.$refs;

    flicking.on('ready', () => {
      this.initFlicking();
    });
    flicking.on('panelChange', () => {
      this.initFlicking();
    });
  },
  methods: {
    resizeFlickingCamera() {
      const { flicking } = this.$refs;
      flicking.resize();
      this.setPageCount();
    },
    setPageCount() {
      const { flicking } = this.$refs;

      if (flicking.camera.range.max === 0) {
        this.pageCount = 1;
      } else {
        this.pageCount = Math.ceil(flicking.camera.range.max / flicking.viewport.width);
      }
    },
    addDragStart() {
      const children = this.$el.querySelectorAll('.carousel-cell');

      children.forEach((el) => {
        el.ondragstart = function preventDrag() { return false; }; // eslint-disable-line no-param-reassign, max-len
      });
    },
    removeDragStart() {
      const children = this.$el.querySelectorAll('.carousel-cell');

      children.forEach((el) => {
        el.ondragstart = undefined; // eslint-disable-line no-param-reassign
      });
    },
    initFlicking() {
      const { flicking } = this.$refs;
      // hack to fix bad initial position
      const panel = flicking.getPanel(0);
      if (panel && flicking.control) {
        flicking.control.setActive(panel);
      }

      flicking.on('holdStart', this.addDragStart);

      flicking.on('holdEnd', this.removeDragStart);

      if (this.arrows) {
        flicking.addPlugins(
          new Arrow({
            parentEl: typeof document !== 'undefined' && document.body,
            prevElSelector: `.${this.prevClass}`,
            nextElSelector: `.${this.nextClass}`,
            moveByViewportSize: true,
          }),
        );
        flicking.resize();
        this.setPageCount();
      }
      if (this.autoplay > 0) {
        flicking.addPlugins(
          new AutoPlay({ duration: this.autoplay }),
        );
      }
      if (this.pagination) {
        flicking.addPlugins(
          new Pagination({
            type: 'bullet',
            parentEl: typeof document !== 'undefined' && document.body,
            selector: `.${this.paginationClass}`,
          }),
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.flicking-pagination {
  position: absolute;
  left: 0;
  bottom: 10px;
  width: 100%;
  text-align: center;
  z-index: 2;
}

.flicking-pagination-bullets,
.flicking-pagination-scroll {
  font-size: 0;
}

.flicking-pagination-scroll {
  left: 50%;
  transform: translate(-50%);
  white-space: nowrap;
  overflow: hidden;
}

.flicking-pagination-scroll .flicking-pagination-slider {
  transition: 0.2s transform;
}

.flicking-pagination-bullet {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 4px;
  border-radius: 50%;
  background-color: #c7ced7;
  cursor: pointer;
  font-size: 1rem;
}

.flicking-pagination-scroll .flicking-pagination-bullet {
  vertical-align: middle;
  position: relative;
  transition: 0.2s transform, 0.2s left;
  transform: scale(0);
}

.flicking-pagination-scroll.flicking-pagination-uninitialized .flicking-pagination-slider,
.flicking-pagination-scroll.flicking-pagination-uninitialized .flicking-pagination-bullet {
  transition: none;
}

.flicking-pagination-bullet-active {
  background-color: #0e6ef4;
}

.flicking-pagination-scroll .flicking-pagination-bullet-active {
  transform: scale(1);
}

.flicking-pagination-scroll .flicking-pagination-bullet-prev,
.flicking-pagination-scroll .flicking-pagination-bullet-next {
  transform: scale(0.66);
}

.flicking-pagination-scroll .flicking-pagination-bullet-prev2,
.flicking-pagination-scroll .flicking-pagination-bullet-next2 {
  transform: scale(0.33);
}
</style>

<style>
.flicking-viewport {
  position: relative;
  overflow: hidden;
}

.flicking-camera {
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: row;
  z-index: 1;
}

.flicking-camera > * {
  flex-shrink: 0;
}

.flicking-viewport.vertical {
  display: inline-flex;
}

.flicking-viewport.vertical > .flicking-camera {
  display: inline-flex;
  flex-direction: column;
}

.flicking-viewport.flicking-hidden .flicking-camera > * {
  visibility: hidden;
}
</style>

<style lang="scss">
@import '~/styles/utils/breakpoints';

.flicking-arrow-prev,
.flicking-arrow-next {
  position: absolute;
  top: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  transform: translateY(-50%);
  z-index: 2;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

.flicking-arrow-prev.is-circle,
.flicking-arrow-next.is-circle {
  background: #09292b;
  border-radius: 50%;
}

.flicking-arrow-prev {
  left: 10px;
}

.flicking-arrow-next {
  right: 10px;
}

.flicking-arrow-prev.is-outside {
  left: -74px;
}

.flicking-arrow-next.is-outside {
  right: -74px;
}

.flicking-arrow-prev::before,
.flicking-arrow-prev::after,
.flicking-arrow-next::before,
.flicking-arrow-next::after {
  content: '';
  width: 14px;
  height: 1px;
  position: absolute;
  background-color: rgba(255, 255, 255, 0.75);
}

.flicking-arrow-prev::before {
  top: 50%;
  left: 14px;
  transform: rotate(-45deg);
  transform-origin: 0% 50%;
}

.flicking-arrow-prev::after {
  top: 50%;
  left: 14px;
  transform: rotate(45deg);
  transform-origin: 0% 50%;
}

.flicking-arrow-next::before {
  top: 50%;
  right: 14px;
  transform: rotate(45deg);
  transform-origin: 100% 50%;
}

.flicking-arrow-next::after {
  top: 50%;
  right: 14px;
  transform: rotate(-45deg);
  transform-origin: 100% 50%;
}

@include media-breakpoint-down(sm) {
  .flicking-button {
    display: none;
  }
}
</style>

<style scoped>
.flicking-arrow-prev {
  left: -20px;
}

.flicking-arrow-next {
  right: -20px;
}

.flicking-arrow-is-disabled {
  display: none;
}

.wrapper {
  position: relative;
  width: 100%;
}
</style>
